import { MyRef } from "./myref.js";
import { LocalizationManager } from "../localization/localization_manager.js";

export function delay( ms: number ): Promise<void>
{
  return new Promise( ( resolve ) => setTimeout( resolve, ms ) );
}

export function hexToBytes( hex: string ): number[]
{
  const bytes: number[] = [];
  for ( let k = 0; k < hex.length; k += 2 )
    bytes.push( parseInt( hex.substring( k, k + 2 ), 16 ) );

  return bytes;
}
export function hexFromBytes( arr: number[] ): string
{
  const res = arr
    .map( byte => byte.toString( 16 ).padStart( 2, '0' ) )
    .join( '' );

  return res;
}

export function randomRange( min: number, max: number ): number
{
  if ( max === min )
    return min;

  return min + Math.random() * ( max - min );
}

export function randomSign(): 1 | -1
{
  return Math.random() < 0.5 ? -1 : 1;
}

export function max2( v1:number, v2:number ):number
{
  return v1 > v2 ? v1 : v2;
}
export function min2( v1:number, v2:number ):number
{
  return v1 < v2 ? v1 : v2;
}

export function updMax( max_val:MyRef<number>, val:number ):void
{
  if ( max_val.value < val )
    max_val.value = val;
}

export function applyMin( val:MyRef<number>, min_val:number ):void
{
  if ( val.value < min_val )
    val.value = min_val;
}

export function updMin( min_val:MyRef<number>, val:number ):void
{
  if ( min_val.value > val )
    min_val.value = val;
}

export function applyMax( val:MyRef<number>, max_val:number ):void
{
  if ( val.value > max_val )
    val.value = max_val;
}

export function toRange( val:MyRef<number>, min_val:number, max_val:number ):void
{
  if ( val.value < min_val )
    val.value = min_val;
  else
  if ( val.value > max_val )
    val.value = max_val;
}

export function sqr( x:number ):number
{
  return x * x;
}

export function fmt2( n: number ):string
{
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function fmt3( n: number ):string
{
  return n.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

export function stringHashCode( str: string ): number
{
  let hash = 0;
  if ( str.length === 0 ) return hash;

  for ( let i = 0; i < str.length; i++ )
  {
    const chr = str.charCodeAt( i );
    hash = ( hash << 5 ) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
}

// Convert int to abbreviation B-M-K

export interface AbbreviationSettings
{
  billion_round_digits: number;
  million_round_digits: number;
  thousand_round_digits: number;
}

const DEFAULT_SETTINGS: AbbreviationSettings = {
  billion_round_digits: 2,
  million_round_digits: 2,
  thousand_round_digits: 2,
};

export function toAbbreviation(
  value: number,
  settings: AbbreviationSettings = DEFAULT_SETTINGS,
  minimal_thousand_value: number = 100
): string
{
  if ( value / 1e9 >= 1 )
  {
    const v = roundTo( value / 1e9, settings.billion_round_digits );
    return `${v}B`;
  }
  if ( value / 1e6 >= 1 )
  {
    const v = roundTo( value / 1e6, settings.million_round_digits );
    return `${v}M`;
  }
  if ( value / 1e3 >= minimal_thousand_value )
  {
    const v = roundTo( value / 1e3, settings.thousand_round_digits );
    return `${v}K`;
  }
  return value.toString();
}

function roundTo( num: number, digits: number ): string
{
  const s = num.toFixed( digits );

  return s.replace( /\.?0+$/, "" );
}
/**
 * Adaptive countdown showing the two most significant units, from years down
 * to seconds: "1р 2міс" / "2міс 5д" / "2д 5г" / "5г 12хв" / "12хв 30с" / "30с".
 * Months are 30 days, years are 12 such months. Unit abbreviations come from
 * localization, so it reads right in every language.
 */
export function formatShortDuration( ms: number ): string
{
  // Round up so a running countdown never shows 0с while time still remains.
  const total_seconds = Math.ceil( Math.max( 0, ms ) / 1000 );
  const seconds = total_seconds % 60;
  const total_minutes = Math.floor( total_seconds / 60 );
  const minutes = total_minutes % 60;
  const total_hours = Math.floor( total_minutes / 60 );
  const hours = total_hours % 24;
  const total_days = Math.floor( total_hours / 24 );
  const days = total_days % 30;
  const total_months = Math.floor( total_days / 30 );
  const months = total_months % 12;
  const years = Math.floor( total_months / 12 );

  const unit = ( key: string ): string => LocalizationManager.t( `TIME.SHORT_${key}` );

  if ( years > 0 ) return `${years}${unit( "YEARS" )} ${months}${unit( "MONTHS" )}`;
  if ( total_months > 0 ) return `${months}${unit( "MONTHS" )} ${days}${unit( "DAYS" )}`;
  if ( total_days > 0 ) return `${days}${unit( "DAYS" )} ${hours}${unit( "HOURS" )}`;
  if ( total_hours > 0 ) return `${hours}${unit( "HOURS" )} ${minutes}${unit( "MINUTES" )}`;
  if ( total_minutes > 0 ) return `${minutes}${unit( "MINUTES" )} ${seconds}${unit( "SECONDS" )}`;
  return `${seconds}${unit( "SECONDS" )}`;
}
